// File: src/components/mixer/MixerPortalNavBar.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { FaUsers } from 'react-icons/fa';
import { ReactComponent as TribeLogo } from '../../../assets/TribeLogo.svg';
import EventHelpToolTipLeft from './EventHelpToolTipLeft'
import axios from 'axios';

/* ───────── STYLES ───────── */
const S = {
  wrapper: {
    position: 'sticky',
    top: 0,
    zIndex: 100,
    background: '#fff',
    fontFamily:
      "'Inter', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
  },
  navRow: {
    height: 55,
    maxWidth: 1200,
    margin: '0 auto',
    padding: '0 12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    position: 'relative', // for absolute logo centering
  },

  /* Exit button */
  exitBtn: {
    appearance: 'none',
    background: '#fff',
    color: '#374151',
    border: '1px solid #e5e7eb',
    borderRadius: 9999,
    padding: '6px 12px',
    fontSize: '0.8rem',
    fontWeight: 600,
    lineHeight: 1,
    height: 28,
    cursor: 'pointer',
    zIndex: 1, // ensure above centered logo
  },

  /* Tooltip holder aligns like the exit button (left side) */
  helpWrap: {
    display: 'flex',
    alignItems: 'center',
    zIndex: 1,
    height: 28,
  },

  /* Center logo — always true center */
  logoCenter: {
    position: 'absolute',
    left: '50%',
    transform: 'translateX(-50%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    pointerEvents: 'none',
  },
  logo: {
    height: 'auto',
    width: 72,
  },

  /* Occupancy pill */
  occPill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    background: '#f8fafc',
    color: '#475569',
    border: '1px solid #e5e7eb',
    borderRadius: 9999,
    padding: '0px 10px',
    fontSize: '0.8rem',
    fontWeight: 600,
    lineHeight: 1,
    height: 28,
    zIndex: 1,
  },
  occIcon: {
    fontSize: 12,
    opacity: 0.85,
  },
};

/**
 * Props:
 *  - mixerEventId (string)       required for occupancy polling
 *  - isPortalPage (boolean?)     optional override; if omitted we detect via URL
 *  - helpTitle, helpBody (string) optional tooltip copy when not on portal
 */
export default function MixerPortalHeaderBar({
  mixerEventId,
  isPortalPage,
  helpTitle = 'Need a hand?',
  helpBody = 'Quick tips for this step. If you get stuck, look for the timer and follow the on-screen prompts.',
}) {
  const [count, setCount] = useState(0);
  const navigate  = useNavigate();
  const location  = useLocation();
  const { eventId: routeEventId } = useParams();

  // Resolve the event id: prefer prop, else URL param; strip "mixer-" if present
  const resolvedEventId = String(mixerEventId || routeEventId || '')
    .replace(/^mixer-/, '')
    .trim();

  // Auto-detect portal by URL if prop not provided
  const inferredIsPortal =
    typeof isPortalPage === 'boolean'
      ? isPortalPage
      : /portal/i.test(location.pathname || '');

  useEffect(() => {
    if (!resolvedEventId) return;

    const fetchOccupancy = async () => {
      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_API_URL}/mixer/events/${resolvedEventId}`,
          { headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` } }
        );
        if (typeof data.occupancyCount === 'number') setCount(data.occupancyCount);
      } catch (err) {
        console.error('[MixerPortalHeaderBar] occupancy fetch', err);
      }
    };

    fetchOccupancy();
    const id = setInterval(fetchOccupancy, 5000);
    return () => clearInterval(id);
  }, [resolvedEventId]);

  const handleExit = () => {
    if (!resolvedEventId) return navigate('/events');
    const previewId = (mixerEventId && mixerEventId.startsWith('mixer-'))
      ? mixerEventId
      : `mixer-${resolvedEventId}`;
    navigate(`/events/${previewId}`);
  };

  return (
    <div style={S.wrapper}>
      <div style={S.navRow}>
        {/* Left: Exit on portal; Tooltip on other steps */}
        {inferredIsPortal ? (
          <button style={S.exitBtn} onClick={handleExit}>
            Exit
          </button>
        ) : (
          <div style={S.helpWrap}>
            <EventHelpToolTipLeft title={helpTitle} body={helpBody} />
          </div>
        )}

        {/* Center: Tribe Logo (absolute, true center) */}
        <div style={S.logoCenter}>
          <TribeLogo style={S.logo} />
        </div>

        {/* Right: Occupancy */}
        <div style={S.occPill} aria-label="Occupancy">
          <FaUsers style={S.occIcon} />
          <span>{count}</span>
        </div>
      </div>
    </div>
  );
}
